import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Eye, EyeOff, Check, X, ArrowLeft } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password strength rules
const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper',  label: 'One uppercase letter',  test: (p) => /[A-Z]/.test(p) },
  { id: 'lower',  label: 'One lowercase letter',  test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number',            test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'One special character', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function getStrength(password) {
  if (!password) return { label: '-', score: 0, color: 'text-slate-500', bar: 'bg-slate-700' };
  const passed = PASSWORD_RULES.filter(r => r.test(password)).length;
  if (passed <= 2) return { label: 'Weak',   score: passed, color: 'text-red-400',     bar: 'bg-red-500' };
  if (passed <= 4) return { label: 'Medium', score: passed, color: 'text-amber-400',   bar: 'bg-amber-500' };
  return                  { label: 'Strong', score: passed, color: 'text-emerald-400', bar: 'bg-emerald-500' };
}

export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'signin' ? 'signin' : 'signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firm_name: ''
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const ruleStatus = useMemo(() =>
    PASSWORD_RULES.map(r => ({ ...r, ok: r.test(formData.password) })),
    [formData.password]
  );
  const strength = useMemo(() => getStrength(formData.password), [formData.password]);
  const allRulesPassed = ruleStatus.every(r => r.ok);
  const isSignup = mode === 'signup';
  const submitDisabled = loading || (isSignup && (!allRulesPassed || !termsAccepted));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSignup && !allRulesPassed) {
      setError('Please meet all password requirements before creating your account.');
      return;
    }
    if (isSignup && !termsAccepted) {
      setShowTermsError(true);
      setError('You must agree to the Terms & Conditions and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    setError('');
    setShowTermsError(false);

    try {
      if (isSignup) {
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          firm_name: formData.firm_name,
          subscription_plan: 'Growth',
          terms_accepted: true
        });
        onLogin(response.data.token, response.data.user, response.data.firm);
        navigate('/onboarding');
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        onLogin(response.data.token, response.data.user, response.data.firm);
        if (response.data.firm.onboarding_completed) {
          navigate('/dashboard');
        } else {
          navigate('/onboarding');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-black text-white flex items-center justify-center p-6 overflow-hidden">
      {/* Background gradient + glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#1a1033_0%,_#000000_60%)]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-violet-600/20 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-600/15 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative w-full max-w-md">
        {/* Back to home */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 mb-5 text-sm text-slate-400 hover:text-violet-300 transition-colors"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>

        {/* Logo + Brand */}
        <div className="text-center mb-8">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex flex-col items-center justify-center group cursor-pointer"
            aria-label="Go to home page"
            data-testid="auth-logo-home-link"
          >
            <span className="inline-flex items-center justify-center mb-4 transition-transform group-hover:scale-105">
              <BrandLogo size={56} />
            </span>
            <h1 className="text-3xl font-bold tracking-tight group-hover:text-violet-200 transition-colors" style={{ fontFamily: 'Outfit' }} data-testid="auth-brand">
              Law Agency 360
            </h1>
          </button>
        </div>

        {/* Auth card */}
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl shadow-violet-900/30">
          <div className="mb-6">
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }} data-testid="auth-heading">
              {isSignup ? 'Create your Law Agency 360 account' : 'Welcome back'}
            </h2>
            <p className="text-slate-400 text-sm mt-1.5" data-testid="auth-subtext">
              {isSignup ? 'Get full visibility into your marketing, in real time' : 'Sign in to your Law Agency 360 account'}
            </p>
          </div>

          {error && (
            <div className="mb-5 p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3" data-testid="error-message">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignup && (
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5 uppercase tracking-wide">
                  Firm Name
                </label>
                <input
                  type="text"
                  name="firm_name"
                  value={formData.firm_name}
                  onChange={handleChange}
                  required
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500/40 transition-all"
                  data-testid="input-firm-name"
                  placeholder="Your law firm name"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500/40 transition-all"
                data-testid="input-email"
                placeholder="you@lawfirm.com"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5 uppercase tracking-wide">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full h-11 pl-4 pr-11 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500/40 transition-all"
                  data-testid="input-password"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {isSignup && formData.password && (
                <div className="mt-3 space-y-2.5" data-testid="password-strength">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <span key={i}
                          className={`h-1 w-7 rounded-full transition-colors ${i <= strength.score ? strength.bar : 'bg-white/10'}`}
                        />
                      ))}
                    </div>
                    <span className={`text-xs font-semibold ${strength.color}`} data-testid="password-strength-label">
                      {strength.label}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {ruleStatus.map(r => (
                      <li key={r.id} className="flex items-center gap-2 text-xs" data-testid={`pwd-rule-${r.id}`}>
                        {r.ok ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <X className="w-3.5 h-3.5 text-slate-500" />
                        )}
                        <span className={r.ok ? 'text-slate-200' : 'text-slate-500'}>{r.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {isSignup && (
              <div className="space-y-2" data-testid="terms-block">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setShowTermsError(false); }}
                    required
                    aria-required="true"
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-violet-500 accent-violet-500 cursor-pointer"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline" data-testid="terms-link">Terms &amp; Conditions</a>
                    {' '}and{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-violet-300 hover:text-violet-200 underline" data-testid="privacy-link">Privacy Policy</a>
                  </span>
                </label>
                {showTermsError && (
                  <div className="flex items-center gap-2 text-xs text-red-400" data-testid="terms-error">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>Please accept the Terms &amp; Conditions and Privacy Policy to continue.</span>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitDisabled}
              className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
              data-testid="submit-button"
            >
              {loading ? 'Processing...' : isSignup ? 'Create Account' : 'Sign In'}
            </button>

            {!isSignup && (
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => navigate('/auth/forgot-password')}
                  className="text-sm text-slate-400 hover:text-violet-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => { setMode(isSignup ? 'signin' : 'signup'); setError(''); }}
              className="text-sm text-slate-400 hover:text-violet-300 transition-colors"
              data-testid="toggle-mode"
            >
              {isSignup ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          The only digital marketing agency built exclusively for law firms.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-slate-500" data-testid="auth-legal-links">
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-violet-300 transition-colors" data-testid="auth-terms-link">Terms &amp; Conditions</a>
          <span aria-hidden>•</span>
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-violet-300 transition-colors" data-testid="auth-privacy-link">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}
